import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAccountDataExport } from "@/hooks/useAccountDataExport";
import {
  exportErrorMessage,
  exportStatusText,
  formatBytes,
  isExportActive,
  isExportDownloadable,
  partLabel,
} from "@/lib/account-data-export";

export interface AccountDataExportPanelProps {
  organizationId: string | null;
  compact?: boolean;
}

const INCLUDED = [
  "Contactos con sus atributos, etiquetas y notas",
  "Conversaciones con todos sus mensajes, notas internas y enlaces a los adjuntos",
  "Equipo, roles, colas y etiquetas",
  "Asistentes de IA y de voz, bases de conocimiento, flujos y automatizaciones",
  "Campañas con sus destinatarios y resultados, formularios y reservas",
  "Historial de facturación: pagos, periodos y movimientos de créditos",
];

export function AccountDataExportPanel({ organizationId, compact = false }: AccountDataExportPanelProps) {
  const { status, loading, requesting, error, request } = useAccountDataExport(organizationId);
  const job = status?.job ?? null;
  const active = isExportActive(job);
  const downloadable = isExportDownloadable(job);
  const statusText = exportStatusText(status);
  const parts = downloadable && job ? job.parts.filter((part) => !!part.url) : [];
  const blocked = !!status && !status.can_request && !active;
  const canRequest = !loading && !requesting && !active && !blocked;

  return (
    <div className="space-y-3" data-testid="account-data-export">
      {!compact && (
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          {INCLUDED.map((item) => <li key={item}>{item}</li>)}
        </ul>
      )}

      {parts.length > 0 && (
        <div className="flex flex-col gap-2">
          {parts.map((part) => (
            <a
              key={part.part}
              href={part.url ?? undefined}
              download={part.file_name}
              className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              {partLabel(part, parts.length)}
              {formatBytes(part.bytes) && <span className="opacity-80">· {formatBytes(part.bytes)}</span>}
            </a>
          ))}
        </div>
      )}

      {!downloadable && (
        <Button type="button" onClick={() => void request()} disabled={!canRequest} className="min-h-11 w-full">
          {requesting || active
            ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            : <Download className="h-4 w-4" aria-hidden="true" />}
          {active ? "Preparando tus datos…" : "Descargar mis datos"}
        </Button>
      )}

      {statusText && <p className="text-sm text-muted-foreground" role="status">{statusText}</p>}
      {blocked && status?.blocked_reason && (
        <p className="text-sm text-muted-foreground">{exportErrorMessage(status.blocked_reason)}</p>
      )}
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

      {!compact && (
        <p className="text-xs text-muted-foreground">
          Te enviamos un correo cuando la descarga esté lista. Los enlaces duran 7 días. Puedes pedir hasta 3 descargas al día.
          Por seguridad no incluimos tokens de los canales, claves ni datos de tarjetas.
        </p>
      )}
    </div>
  );
}
